import { h, type Component, type VNode } from 'vue'

/**
 * TanStack Table column interface for header render function.
 */
interface SortableColumn {
  getIsSorted: () => false | 'asc' | 'desc'
  toggleSorting: (desc?: boolean) => void
}

/**
 * Copy text to clipboard.
 */
export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text).catch((err) => {
    console.error('Failed to copy to clipboard:', err)
  })
}

/**
 * Creates a factory for copyable ID cells. Call once in setup with resolved UButton.
 *
 * Usage:
 *   const UButton = resolveComponent('UButton')
 *   const copyableId = createCopyableIdCell(UButton)
 *   // then use: cell: ({ row }) => copyableId(row.original.id)
 */
export function createCopyableIdCell(UButton: Component): (id: string) => VNode {
  return function copyableIdCell(id: string) {
    return h('div', { class: 'flex items-center gap-1' }, [
      h(
        'span',
        {
          class: 'font-mono text-xs text-muted truncate max-w-[140px]',
          title: id,
        },
        id,
      ),
      h(UButton, {
        icon: 'i-lucide-copy',
        size: '2xs',
        variant: 'ghost',
        color: 'neutral',
        class: 'cursor-pointer',
        onClick: (e: Event) => {
          e.stopPropagation()
          copyToClipboard(id)
        },
      }),
    ])
  }
}

/**
 * Creates a factory for sortable headers. Call once in setup with resolved UButton.
 *
 * Usage:
 *   const UButton = resolveComponent('UButton')
 *   const sortable = createSortableHeader(UButton)
 *   // then use: header: sortable('Name')
 */
export function createSortableHeader(UButton: Component) {
  return function sortableHeader(label: string) {
    return ({ column }: { column: SortableColumn }) => {
      const sorted = column.getIsSorted()
      const icon = sorted
        ? sorted === 'asc'
          ? 'i-lucide-arrow-up-narrow-wide'
          : 'i-lucide-arrow-down-wide-narrow'
        : 'i-lucide-arrow-up-down'

      return h(UButton, {
        color: 'neutral',
        variant: 'ghost',
        label,
        icon,
        class: '-mx-2.5',
        onClick: (e: Event) => {
          e.preventDefault()
          column.toggleSorting()
        },
      })
    }
  }
}
